import { useEffect, useState, useCallback } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Info } from "lucide-react";
import api from "@/lib/api";

// Cache of glossary terms loaded once
let glossaryCache = null;
async function loadGlossary() {
  if (glossaryCache) return glossaryCache;
  try {
    const r = await api.get("/glossary");
    glossaryCache = r.data.terms.reduce((acc, t) => { acc[t.id] = t; return acc; }, {});
  } catch {
    glossaryCache = {};
  }
  return glossaryCache;
}

// Seen-terms state (loaded once per session)
let seenCache = null;
async function loadSeen() {
  if (seenCache) return seenCache;
  try {
    const r = await api.get("/me/seen-terms");
    seenCache = new Set(r.data.seen_terms || []);
  } catch {
    seenCache = new Set();
  }
  return seenCache;
}

async function markSeen(termId) {
  if (!seenCache) await loadSeen();
  if (seenCache.has(termId)) return;
  seenCache.add(termId);
  try { await api.post("/me/seen-terms", { term_id: termId }); } catch {}
}

export default function Term({ k, children }) {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(true); // optimistic — hide icon until we know
  const [term, setTerm] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [g, s] = await Promise.all([loadGlossary(), loadSeen()]);
      if (cancelled) return;
      setTerm(g[k] || null);
      setSeen(s.has(k));
    })();
    return () => { cancelled = true; };
  }, [k]);

  const handleOpen = useCallback((nextOpen) => {
    setOpen(nextOpen);
    if (nextOpen && !seen) {
      markSeen(k);
      setSeen(true);
    }
  }, [k, seen]);

  if (!term) return <>{children}</>;

  // If already seen, just render children with subtle dotted underline (still discoverable)
  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`term-${k}`}
          className="inline cursor-help text-left"
          style={{
            color: "inherit",
            textAlign: "left",
            textDecoration: seen ? "underline dotted rgba(184,149,74,0.4)" : "none",
            textUnderlineOffset: "3px",
          }}
        >
          {children}
          {!seen && (
            <span
              className="inline-flex items-center justify-center ml-1 rounded-full align-middle"
              style={{
                width: "16px", height: "16px",
                backgroundColor: "rgba(184,149,74,0.18)",
                color: "var(--vedari-gold)",
                fontSize: "10px",
                fontWeight: 600,
              }}
              data-testid={`term-icon-${k}`}
            >
              <Info size={11} strokeWidth={2} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom" align="start"
        className="w-72 p-4 rounded-xl"
        style={{ backgroundColor: "var(--vedari-surface)", borderColor: "var(--vedari-divider)" }}
      >
        <p className="font-serif text-h3" style={{ color: "var(--vedari-gold)" }} data-testid={`term-title-${k}`}>
          {term.term}
          {term.devanagari && <span className="ml-2 text-body-sm font-normal" style={{ color: "var(--vedari-ink-soft)" }}>{term.devanagari}</span>}
        </p>
        <p className="text-body-sm leading-relaxed mt-3" style={{ color: "var(--vedari-ink)" }}>
          {term.short}
        </p>
        <a
          href="/app/glossary"
          onClick={(e) => { e.preventDefault(); window.location.href = "/app/glossary"; }}
          className="text-caption mt-3 inline-block underline"
          style={{ color: "var(--vedari-slate)" }}
          data-testid={`term-glossary-${k}`}
        >
          See full Vedic glossary
        </a>
      </PopoverContent>
    </Popover>
  );
}
